import { requestInvoice, requestTickets, setCustomer } from "@/redux/ducks/customer";
import { ReducersTypes } from "@/redux/reducers/Models";
import { DebtsByInvoice, getFormattedDebtsByInvoice, getFormattedTickets, Tickets } from "@/utils/transform";
import jsPDF from "jspdf";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

export interface Invoice {
    name: string,
    invoiceId: string,
    invoiceDate: string,
    price: string,
    debts: {
        plate: string;
        date: string;
        price: string;
        originalPrice: string;
        fine: string;
        owner: string;
    }[];
}

interface UI {
    isLoading: boolean;
    errorTickets: boolean;
    errorInvoice: boolean;
    isVisibleModal: boolean;
    invoice: Invoice | null;
}

const useTicket = () => {
    const dispatch = useDispatch();
    const customer = useSelector((state: ReducersTypes) => state.customerReducer);

    const [ui, setUi] = useState<UI>({
        isLoading: false,
        errorTickets: false,
        errorInvoice: false,
        isVisibleModal: false,
        invoice: null,
    });

    const toggleModal = () => setUi(prev => ({ ...prev, errorInvoice: false, errorTickets: false, isVisibleModal: false }));

    const generatePDF = () => {
        const {
            name,
            invoiceId,
            invoiceDate,
            price,
            debts,
        } = ui.invoice!;

        const tollsPrice = debts?.reduce(
            (sum: number, item) => sum + Number(item?.originalPrice.replace(/,/g, '.')),
            0
        );
        const fines = debts?.reduce(
            (sum: number, item) => sum + Number(item?.fine.replace(/,/g, '.')),
            0
        );

        const doc = new jsPDF();

        const pageHeight = doc.internal.pageSize.height;
        const marginTop = 10;
        let currentY = 50;

        const logo = "/pdfimage.png";
        doc.addImage(logo, "PNG", 70, marginTop, 70, 20.64);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text("Confirmação de pagamento de Ticket", 105, marginTop + 30, { align: "center" });

        currentY = marginTop + 40;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);
        doc.text("Olá, " + name, 20, currentY);
        doc.text(
            "Seu pagamento de R$ " + price + " foi realizado com sucesso. Confira os detalhes a seguir.",
            20,
            currentY + 8
        );

        currentY += 20;
        doc.setFont("helvetica", "bold");
        doc.text("Dados da transação #" + invoiceId, 20, currentY);

        currentY += 5;
        const addTransactionRow = (label: string, value: string) => {
            if (currentY > pageHeight - 20) {
                doc.addPage();
                currentY = marginTop;
            }
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.rect(20, currentY, 170, 8);
            doc.text(label, 22, currentY + 5);
            doc.text(value, 190 - 5, currentY + 5, { align: "right" });
            currentY += 8;
        };

        addTransactionRow("Data:", invoiceDate);
        addTransactionRow("Passagens:", "R$ " + tollsPrice?.toFixed(2)?.replace(/\./g, ','));
        addTransactionRow("Multas e juros:", "R$ " + fines?.toFixed(2)?.replace(/\./g, ','));
        addTransactionRow("Total:", "R$ " + price);

        currentY += 10;
        doc.setFont("helvetica", "bold");
        doc.text("Detalhes do pagamento", 20, currentY);

        currentY += 10;
        debts?.forEach(item => {
            if (currentY > pageHeight - 40) {
                doc.addPage();
                currentY = marginTop + 10;
            }

            addTransactionRow("Placa:", item?.plate || "N/A");
            addTransactionRow("Passagem:", "R$ " + (item?.originalPrice || "0,00"));
            addTransactionRow("Multas e juros:", "R$ " + (item?.fine || "0,00"));
            addTransactionRow("Total:", "R$ " + (item?.price || "0,00"));
            addTransactionRow("Nome Concessionária:", item?.owner || "Detalhe se houver");
            currentY += 10;
        });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text(
            "Recibo gerado em " + new Date().toLocaleString("pt-BR"),
            20,
            pageHeight - 10
        );

        doc.save(invoiceId + ".pdf");
    };

    const handleRequestInvoice = useCallback((invoice: {
        date: string;
        price: string;
        id: string;
        status: string;
    }, success: boolean, code?: string | DebtsByInvoice[]) => {
        setUi(prev => ({ ...prev, isLoading: false }));

        if (!success) {
            setUi(prev => ({ ...prev, errorInvoice: false }));
            return;
        }

        setUi(prev => ({
            ...prev,
            isVisibleModal: true,
            invoice: {
                name: customer?.client?.name!,
                invoiceId: invoice.id,
                invoiceDate: invoice.date,
                price: invoice.price,
                debts: getFormattedDebtsByInvoice(code! as DebtsByInvoice[]),
            }
        }));
    }, [customer]);

    const getInvoice = useCallback((invoice: {
        date: string;
        price: string;
        id: string;
        status: string;
    }) => {
        if (ui.isLoading) return;

        setUi(prev => ({ ...prev, isLoading: true, errorTickets: false, errorInvoice: false }));

        dispatch(
            requestInvoice({
                invoiceId: invoice?.id,
                callback: (success, code) => handleRequestInvoice(invoice, success, code),
            })
        );
    }, [dispatch, ui.isLoading, handleRequestInvoice, customer]);

    const handleRequestTickets = useCallback(
        ({ success, code, callback }: { success: boolean; code?: string | Tickets[]; callback?: () => void }) => {
            setUi(prev => ({ ...prev, isLoading: false }));

            if (!success) {
                setUi(prev => ({ ...prev, errorTickets: false }));
                dispatch(
                    setCustomer({
                        tickets: [],
                    })
                );
                callback?.();
                return;
            }

            dispatch(
                setCustomer({
                    tickets: getFormattedTickets(code! as Tickets[]),
                })
            );

            callback?.();
        },
        [dispatch]
    );

    const getTickets = useCallback((callback?: () => void) => {
        if (ui.isLoading) return;

        setUi(prev => ({ ...prev, isLoading: true, errorTickets: false, errorInvoice: false }));

        dispatch(
            requestTickets({
                callback: (success, code) => handleRequestTickets({
                    success,
                    code,
                    callback,
                }),
            })
        );
    }, [dispatch, handleRequestTickets, ui.isLoading]);

    return {
        toggleModal,
        getInvoice,
        getTickets,
        generatePDF,
        customer,
        ...ui,
    };
};

export default useTicket;